import { useEffect, useState } from 'react';
import { Box, Dialog, Typography, Stack, styled } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import useLocales from '../../hooks/useLocales';
import Iconify from '../../components/Iconify';
import conObj from '../../utils/connection-assistant';
import { useSelector, useDispatch } from '../../redux/store';
import Image from '../../components/Image';
import AccountOTP from './AccountOTP';
import { setIsOpenCS } from '../../redux/slices/settings';
import ChangePassword from '../../pages/auth/ChangePassword';
import { setOpenChangePassword } from '../../redux/slices/authPages';
import { CardTitleAlt } from '../../layouts/GlobalStyles';

const ButtonBox = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 7,
  border: '1px solid #626262',
  height: 47,
  flex: 1,
  cursor: 'pointer',
}));

const Container = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(0deg, rgb(7, 20, 44) -5.86%, rgb(38, 58, 89) 104.05%)',
  padding: '3.5rem 1rem 1rem',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const AccountProfile = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const { translate } = useLocales();

  const { userProfile } = useSelector((x) => x.lookup);

  const { isOpenChangePwd } = useSelector((x) => x.authPages);

  const [isEditEmail, setIsEditEmail] = useState(false);

  const [email, setEmail] = useState(userProfile?.email);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isOpenOtp, setIsOpenOtp] = useState(false);

  const [memberBank, setMemberBank] = useState(null);

  useEffect(() => {
    getMemberBankAccounts();
  }, []);

  const updateEmail = () => {
    if (!email.match('[a-z0-9]+@[a-z]+.[a-z]{2,3}')) {
      const snackbarOptions = { variant: 'error', autoHideDuration: 5000 };
      enqueueSnackbar(translate('invalid_x', { x: translate('email') }), snackbarOptions);
      return false;
    }

    setIsSubmitting(true);

    conObj
      .post('users/edit.json', {
        'user-email': email?.trim(),
      })
      .then((res) => {
        if (res.status === 200) {
          setIsEditEmail(false);
          setIsSubmitting(false);
        }
      })
      .catch((err) => {
        const snackbarOptions = { variant: 'error', autoHideDuration: 5000 };
        enqueueSnackbar(err?.message, snackbarOptions);
        setIsSubmitting(false);
      });
  };

  const getMemberBankAccounts = () => {
    conObj
      .post('members/banks.json')
      .then((res) => {
        if (res.status === 200) {
          if (res.data?.data?.status === 1) {
            // they should have bank here already. but just for the sake of checking
            if (res.data?.data?.data?.length > 0) {
              setMemberBank(res.data?.data?.data[0]);
            }
          }
        }
      })
      .catch((err) => {
        console.log(`Server Error: ${err.message}`);
      });
  };

  return (
    <Box
      sx={{
        maxWidth: '1200px',
        mx: 'auto',
      }}
    >
      <Stack direction="row" sx={{ background: '#2C0202', px: '.5rem', py: '.75rem' }}>
        <Iconify
          onClick={() => navigate(-1)}
          icon={'ic:twotone-arrow-back'}
          sx={{ width: '26px', height: '26px', color: '#fff', cursor: 'pointer' }}
        />
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', textAlign: 'center', flex: 1 }}>
          {translate('profile')}
        </Typography>
      </Stack>

      <Box sx={{ py: '1.5rem', px: '0.75rem' }}>
        <Stack direction="column" alignItems="center" sx={{ mt: 3 }}>
          {/* <Iconify
          icon="mdi:account-circle"
          sx={{
            width: 100,
            height: 100,
            color: 'white',
            borderRadius: '50%',
            border: '3px solid white',
          }}
        /> */}
          <Image
            src={require('../../assets/navbar/profile.png')}
            sx={{ borderRadius: '10px', width: 100, height: 100 }}
          />
          <Typography sx={{ color: 'white', fontSize: '1.125rem', mt: 1 }}>
            {userProfile?.UserProfile?.no_hp}
          </Typography>
        </Stack>

        <Stack alignItems={'center'} sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: 600, color: '#fff', fontSize: '15px' }}>{userProfile?.full_name}</Typography>
          <Stack direction="row" spacing={'5px'} sx={{ alignItems: 'center' }}>
            <Box sx={{ background: '#ddd', borderRadius: '9999px', p: '4px' }}>
              <Image src={require('../../assets/navbar/bank.png')} sx={{ width: '15px', height: '15px' }} />
            </Box>
            <Typography sx={{ fontWeight: 500, color: '#fff', fontSize: '13px' }}>
              {memberBank?.account_number}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={'0.5rem'} sx={{ pt: '1rem' }}>
          <ButtonBox direction="row" spacing={'0.5rem'} onClick={() => dispatch(setIsOpenCS(true))}>
            <Image src={require('../../assets/profile/contact.png')} sx={{ width: '25px', height: '25px' }} />
            <Typography sx={{ fontSize: '.75rem', fontWeight: 700 }}>{translate('edit_account_phone')}</Typography>
          </ButtonBox>
          <ButtonBox direction="row" spacing={'0.5rem'} onClick={() => dispatch(setOpenChangePassword(true))}>
            <Image src={require('../../assets/profile/podlock.png')} sx={{ width: '25px', height: '25px' }} />
            <Typography sx={{ fontSize: '.75rem', fontWeight: 700 }}>{translate('edit_password')}</Typography>
          </ButtonBox>
        </Stack>

        {isOpenOtp && <AccountOTP open={isOpenOtp} onClose={() => setIsOpenOtp(false)} />}

        <Dialog
          PaperProps={{
            sx: {
              m: '1rem',
              position: 'relative',
            },
          }}
          fullWidth
          open={isOpenChangePwd}
          onClose={() => dispatch(setOpenChangePassword(false))}
        >
          <Container>
            <CardTitleAlt sx={{ position: 'absolute', top: 0 }}>{translate('change_password')}</CardTitleAlt>
            <Iconify
              onClick={() => dispatch(setOpenChangePassword(false))}
              icon="ic:baseline-close"
              sx={{
                color: '#fff',
                width: '25px',
                height: '25px',
                position: 'absolute',
                cursor: 'pointer',
                right: '.5rem',
                top: '.5rem',
              }}
            />
            <ChangePassword />
          </Container>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AccountProfile;
